/**
 * Geometria de régua temporal — compartilhada entre a régua de Comunicados e a de Fases por loja.
 *
 * SÓ FUNÇÕES PURAS, e isso é deliberado: o que as duas réguas têm em comum é a MATEMÁTICA
 * (mapear data em percentual, empilhar barras que se sobrepõem, escolher o passo dos ticks). O
 * markup continua sendo de cada tela, porque as duas respondem perguntas diferentes e um componente
 * genérico ficaria cheio de props condicionais.
 *
 * Compartilhar o cálculo evita o que importa evitar: duas réguas que desenham a mesma data em
 * pixels diferentes, ou uma que ganha correção de borda e a outra não.
 */

/**
 * Fábrica de escala: converte data ISO em percentual dentro do intervalo [from, to].
 *
 * Sempre limita a 0–100, e é isso que faz uma barra que começa antes do recorte aparecer colada na
 * borda esquerda em vez de sair da tela com percentual negativo.
 */
export function criarEscala(from, to) {
    const t0 = new Date(from).getTime();
    const t1 = new Date(to).getTime();
    const span = t1 - t0 || 1;          // intervalo de um instante não divide por zero
    return {
        t0,
        t1,
        span,
        /** `fallback` é o timestamp a usar quando a data é nula (barra sem início/fim). */
        pct(iso, fallback) {
            const t = iso ? new Date(iso).getTime() : fallback;
            return Math.max(0, Math.min(100, ((t - t0) / span) * 100));
        },
    };
}

/**
 * Empilha barras em FAIXAS quando as janelas se sobrepõem.
 *
 * Sem isto, barras com a mesma janela ficam desenhadas uma exatamente sobre a outra e só a de cima
 * aparece — o bug some da tela junto com o dado. Cada barra precisa de `left` e `right` em
 * percentual. A folga de 0,05% impede que duas barras encostadas sejam consideradas sobrepostas.
 */
export function empilharFaixas(barras) {
    const faixas = [];
    [...barras]
        .sort((a, b) => a.left - b.left || a.right - b.right)
        .forEach((barra) => {
            const faixa = faixas.find((f) => f[f.length - 1].right <= barra.left - 0.05);
            if (faixa) faixa.push(barra);
            else faixas.push([barra]);
        });
    return faixas;
}

/**
 * Marcas do eixo, com passo adaptativo: dia a dia até 16 dias, de 3 em 3 até 40, senão semanal.
 * Sem isso, um recorte de seis meses vira uma parede de rótulos ilegível.
 */
export function marcasDoEixo(escala, formatarDia) {
    const dias = Math.max(1, Math.round(escala.span / 86400000));
    const passo = dias <= 16 ? 1 : dias <= 40 ? 3 : 7;
    const marcas = [];
    for (let i = 0; i <= dias; i += passo) {
        const d = new Date(escala.t0);
        d.setDate(d.getDate() + i);
        marcas.push({ label: formatarDia(d), left: (i / dias) * 100 });
    }
    return { dias, passo, marcas };
}

/** Posição de "hoje" na régua, ou null quando hoje está fora do recorte. */
export function posicaoDeHoje(escala) {
    const agora = Date.now();
    if (agora < escala.t0 || agora > escala.t1) return null;
    return ((agora - escala.t0) / escala.span) * 100;
}
